import React, { useState, useEffect, useRef } from 'react';
import { BsJournalArrowUp, BsDatabaseCheck, BsDatabaseAdd, BsJournalText, BsHouseDoor } from 'react-icons/bs';
import { FiUser, FiLogOut } from 'react-icons/fi';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import useStore from '../../store/useStore';
import { useNavigate } from 'react-router-dom';

/**
 * Sidebar para navegación entre los diferentes agentes disponibles.
 * Permite al usuario elegir con qué agente quiere interactuar.
 */
const Sidebar = () => {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const accounts = instance.getAllAccounts();
  const account = useAccount(accounts[0] || {});

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhotoUrl, setUserPhotoUrl] = useState('');
  const [userInitials, setUserInitials] = useState('');
  const userMenuRef = useRef(null);
  const { selectedAgent, setSelectedAgent } = useStore();
  const navigate = useNavigate();

  const navigationItems = [
    { key: 'home',        label: 'Inicio',                                      icon: <BsHouseDoor size={24} /> },
    { key: 'LRequerimientos',label: 'Agente de Levantamiento de Requerimientos',icon: <BsJournalArrowUp size={24} /> },
    { key: 'learning',    label: 'Agente de Especificaciones Funcionales',      icon: <BsJournalText size={24} /> },
    { key: 'Lineamientos',label: 'Agente de Lineamientos de Base de Datos',     icon: <BsDatabaseCheck size={24} /> },
    { key: 'Inventory',   label: 'Agente de Inventario de Activos',            icon: <BsDatabaseAdd size={24} /> },
  ];

  const handleNavigation = (key) => {
    if (key === 'home') {
      setSelectedAgent(null);
      navigate('/dashboard');
    } else {
      setSelectedAgent(key);
    }
  };

  // Actualizar información del usuario
  useEffect(() => {
    const updateUserInfo = () => {
      const activeAccount = account || instance.getActiveAccount();
      if (activeAccount) {
        const name = activeAccount.name || activeAccount.idTokenClaims?.name || activeAccount.idTokenClaims?.preferred_username?.split('@')[0] || '';
        setUserName(name);
        const email = activeAccount.username || activeAccount.idTokenClaims?.preferred_username || activeAccount.idTokenClaims?.email || '';
        setUserEmail(email);
        // Para la foto, MSAL podría no proveerla directamente en el token o cuenta.
        // Si tienes una API Graph para obtenerla, aquí sería el lugar.
        // Por ahora, intentaremos con lo que haya en idTokenClaims.
        const photo = activeAccount.idTokenClaims?.picture || activeAccount.idTokenClaims?.photo || '';
        setUserPhotoUrl(photo); // Asumiendo que podrías tenerla
        if (name) {
          setUserInitials(name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase());
        } else if (email) {
          setUserInitials(email[0].toUpperCase());
        } else {
          setUserInitials('U');
        }
      }
    };
    if (isAuthenticated) {
      updateUserInfo();
    } else {
      setUserName('');
      setUserEmail('');
      setUserPhotoUrl('');
      setUserInitials('');
    }
  }, [account, instance, isAuthenticated]);

  // Cerrar menú de usuario al hacer clic fuera
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setUserMenuOpen(false);
    sessionStorage.clear();
    localStorage.clear();
    const logoutUrl = `https://login.microsoftonline.com/2b334cd1-6a14-4f24-9edb-9df51e345f80/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
    window.location.href = logoutUrl;
  };

  return (
    <div
      className={`
        h-full
        w-16 relative /* Nueva anchura */
        bg-white dark:bg-gray-800 shadow-[inset_-3px_0_6px_rgba(0,0,0,0.05)] dark:shadow-[inset_-3px_0_6px_rgba(255,255,255,0.03)]
        text-gray-600 dark:text-gray-300
        flex flex-col
        items-center
        py-6
        border-r border-gray-200 dark:border-gray-700

        /* transition-all ya no aplica al ancho */
      `}
      /* onMouseEnter y onMouseLeave eliminados */
      tabIndex={0}
      aria-label="Barra lateral de navegación"
    >
      <div className="flex flex-col items-center w-full h-full transition-all duration-200">
        {/* Logo */}
        <div className="mb-12 flex items-center justify-center w-full px-4">
          <img src="/Logo512.png" alt="VSISE" className="w-16 h-auto" />
        </div>

        {/* Navegación */}
        <nav className="flex flex-col space-y-1 items-center w-full px-2">
          {navigationItems.map((item) => (
            <div
              key={item.key}
              className="relative w-full flex items-center group"
            >
              <button
                onClick={() => handleNavigation(item.key)}
                className={`
                  flex items-center justify-center w-full p-3 /* Icono centrado, padding general */
                  my-1 rounded-lg
                  transition-colors duration-200 ease-in-out
                  focus:outline-none focus:ring-2 focus:ring-blue-400
                  ${selectedAgent === item.key
                    ? 'bg-indigo-500/10 text-indigo-700 dark:bg-indigo-400/15 dark:text-indigo-200 font-semibold'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-300'
                  }
                `}
                aria-label={item.label}
                tabIndex={0}
                >
                <span className="flex-shrink-0 flex items-center justify-center w-6 h-6">
                  {item.icon}
                </span>
              </button>
              <span className={`absolute left-full ml-3 z-50 px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm rounded-md shadow-lg border border-slate-200 dark:border-slate-700 opacity-0 transform scale-95 group-hover:opacity-100 group-hover:scale-100 ${item.key === 'home' ? '' : 'group-focus-within:opacity-100 group-focus-within:scale-100'} transition-all duration-200 ease-out delay-150 pointer-events-none whitespace-nowrap`}>
                {item.label}
              </span>
            </div>
          ))}
        </nav>

        <div className="flex-1" />

        {/* Menú de Usuario */}
        {isAuthenticated && (
          <div ref={userMenuRef} className="relative group mt-auto mb-6 w-full flex items-center justify-center px-2">
            <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={`
                  flex items-center justify-center w-10 h-10
                  rounded-full bg-gray-200 dark:bg-gray-700
                  border border-gray-300 dark:border-gray-600
                  text-gray-500 dark:text-gray-300
                  opacity-80 hover:opacity-100
                  transition-all duration-200 scale-100 hover:scale-105
                  overflow-hidden
                  focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800
                  mx-auto mb-2
                `}
                aria-label="Perfil y cierre de sesión"
                tabIndex={0}
              >
                {userPhotoUrl ? (
                  <img src={userPhotoUrl} alt="Avatar" className="w-full h-full object-cover grayscale" />
                ) : userInitials ? (
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-200">{userInitials}</span>
                ) : (
                  <FiUser size={18} />
                )}
              </button>
            <span className="absolute left-full ml-3 z-50 px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm rounded-md shadow-lg border border-slate-200 dark:border-slate-700 opacity-0 transform scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 ease-out delay-150 pointer-events-none whitespace-nowrap">
              Perfil y cierre de sesión
            </span>

            {userMenuOpen && (
              <div className="absolute left-full bottom-0 ml-3 mb-0 w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                <div className="px-3 pt-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center mb-6">
                    {userPhotoUrl ? (
                      <img src={userPhotoUrl} alt="Avatar" className="w-10 h-10 rounded-full mr-6 object-cover" />
                    ) : userInitials ? (
                      <div className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center mr-6 text-sm font-semibold">{userInitials}</div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center mr-6"><FiUser size={16} /></div>
                    )}
                    <div>
                      <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{userName || 'Usuario'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userEmail || 'No disponible'}</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-3 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-700/20 focus:outline-none focus:bg-red-50 dark:focus:bg-red-700/20 transition-colors duration-150 ease-in-out"
                >
                  <FiLogOut className="mr-3" size={18} />
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
